import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FaCircleChevronRight } from "react-icons/fa6";
import { FaTimes, FaEnvelope, FaUser, FaBox, FaCheckCircle, FaExclamationTriangle, FaSpinner, FaFileInvoice, FaClock, FaCalendarAlt, FaUsers, FaFilter, FaUndo, FaInfoCircle, FaRegCheckCircle } from "react-icons/fa";
import { GiCancel } from "react-icons/gi";
import { IoMdSend, IoMdClose, IoMdAlert, IoMdCheckmark, IoMdWarning } from "react-icons/io";
import { BsFillExclamationCircleFill, BsFillInfoCircleFill, BsCheckCircle, BsXCircle } from "react-icons/bs";
import { MdEmail, MdError, MdWarning, MdInfo, MdSend } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useGetAllOrderQuery, useUpdateOrderMutation } from "../../services/orderApi";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useCurrency } from '../currency/currencyContext';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from 'sweetalert2';

// Normalize an email so the SAME customer's email always produces the
// SAME grouping key, even if it comes back from the API with different
// casing or stray whitespace (e.g. "John@Gmail.com " vs "john@gmail.com").
const normalizeEmail = (email) => {
  if (!email || typeof email !== "string") return "";
  return email.trim().toLowerCase();
};

export default function Orders() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useGetAllOrderQuery();
  const [updateOrder] = useUpdateOrderMutation();
  const { formatPrice } = useCurrency();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");

  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);
  const [company, setCompany] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10;

  // ✅ FIX: Fetch company info with 1-hour localStorage cache
  useEffect(() => {
    const fetchCompany = async () => {
      const cached = localStorage.getItem('company_info');
      const cachedTime = localStorage.getItem('company_info_time');
      const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

      if (cached && cachedTime && (Date.now() - parseInt(cachedTime, 10) < CACHE_DURATION)) {
        try {
          setCompany(JSON.parse(cached));
          return;
        } catch (e) {
          // fall through to fetch
        }
      }

      try {
        const response = await fetch('https://backend.hira-tech.co.uk/order/company-invoice-details/');
        if (response.ok) {
          const result = await response.json();
          setCompany(result);
          localStorage.setItem('company_info', JSON.stringify(result));
          localStorage.setItem('company_info_time', Date.now().toString());
        }
      } catch (error) {
        console.error('Error fetching company info:', error);
      }
    };
    fetchCompany();
  }, []);

  // Helper function to get valid token
  const getValidToken = async () => {
    let token = localStorage.getItem('access_token') || localStorage.getItem('token');

    if (!token) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await fetch('https://backend.hira-tech.co.uk/api/token/refresh/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refresh: refreshToken }),
          });

          if (response.ok) {
            const result = await response.json();
            localStorage.setItem('access_token', result.access);
            return result.access;
          } else {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('token');
            return null;
          }
        } catch (error) {
          console.error('Token refresh failed:', error);
          return null;
        }
      }
      return null;
    }

    return token;
  };

  // ✅ FIX: Memoize reversed data (reverse only once per data change)
  const reversedData = useMemo(() => {
    if (!data) return [];
    return [...data].reverse();
  }, [data]);

  // ✅ FIX: Memoize unique customers (was a useEffect + useState before)
  const uniqueCustomers = useMemo(() => {
    if (!reversedData.length) return [];
    const customers = new Map();
    reversedData.forEach(order => {
      if (order.customer && order.customer.email) {
        const key = normalizeEmail(order.customer.email);
        if (!customers.has(key)) {
          customers.set(key, {
            email: order.customer.email.trim(),
            name: `${order.customer.first_Name || ''} ${order.customer.last_Name || ''}`.trim(),
            id: order.customer.id
          });
        }
      }
    });
    return Array.from(customers.values());
  }, [reversedData]);

  // ✅ FIX: Memoize filtered orders (was useState + useEffect before)
  const filteredOrders = useMemo(() => {
    if (!reversedData.length) return [];

    let orders = reversedData.filter(order => order.order_Status !== "Canceled");

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      orders = orders.filter((order) => {
        const trackingId = (order.order_Tracking_Id || "").toLowerCase();
        const customerRef = (order.customer_Reference || "").toLowerCase();
        const firstName = (order.customer?.first_Name || "").toLowerCase();
        const lastName = (order.customer?.last_Name || "").toLowerCase();
        const fullName = `${firstName} ${lastName}`.trim();
        return (
          trackingId.includes(lowerSearch) ||
          customerRef.includes(lowerSearch) ||
          firstName.includes(lowerSearch) ||
          lastName.includes(lowerSearch) ||
          fullName.includes(lowerSearch)
        );
      });
    }

    if (selectedStatus) {
      orders = orders.filter((order) => order.order_Status === selectedStatus);
    }

    if (startDate) {
      const start = new Date(startDate);
      orders = orders.filter(
        (order) => new Date(order.order_Placment_Date) >= start
      );
    }

    if (endDate) {
      const end = new Date(endDate);
      orders = orders.filter(
        (order) => new Date(order.order_Placment_Date) <= end
      );
    }

    if (selectedCustomer) {
      const normalizedSelected = normalizeEmail(selectedCustomer);
      orders = orders.filter(
        (order) => normalizeEmail(order.customer?.email) === normalizedSelected
      );
    }

    return orders;
  }, [reversedData, searchTerm, selectedStatus, startDate, endDate, selectedCustomer]);

  // ✅ FIX: Reset page & selection when filters change (separate effect)
  useEffect(() => {
    setCurrentPage(1);
    setSelectedOrders([]);
    setSelectAll(false);
  }, [searchTerm, selectedStatus, startDate, endDate, selectedCustomer]);

  // ✅ FIX: Auto-select orders when date range or customer filter changes
  // (removed filteredOrders.length from deps — was causing extra re-runs)
  useEffect(() => {
    if (startDate || endDate || selectedCustomer) {
      const ordersToSelect = filteredOrders.map(order => order.order_Id);
      setSelectedOrders(ordersToSelect);
      setSelectAll(ordersToSelect.length > 0 && ordersToSelect.length === filteredOrders.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedCustomer]);

  // ✅ FIX: Memoize pagination slice
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = useMemo(
    () => filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder),
    [filteredOrders, indexOfFirstOrder, indexOfLastOrder]
  );
  const totalPages = useMemo(
    () => Math.ceil(filteredOrders.length / ordersPerPage),
    [filteredOrders.length]
  );

  // ✅ FIX: useCallback for select handlers
  const handleSelectOrder = useCallback((orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedOrders(prev => {
      if (selectAll) {
        return [];
      }
      return currentOrders.map(order => order.order_Id);
    });
    setSelectAll(prev => !prev);
  }, [selectAll, currentOrders]);

  // ✅ FIX: useCallback for getSelectedOrderDetails
  const getSelectedOrderDetails = useCallback(() => {
    const selectedSet = new Set(selectedOrders);
    return filteredOrders.filter(order => selectedSet.has(order.order_Id));
  }, [filteredOrders, selectedOrders]);

  // ✅ FIX: Memoize selectedOrderDetails
  const selectedOrderDetails = useMemo(
    () => getSelectedOrderDetails(),
    [getSelectedOrderDetails]
  );

  // ✅ FIX: Memoize groupedOrders (was recalculated on every render)
  const groupedOrders = useMemo(() => {
    const grouped = {};
    selectedOrderDetails.forEach(order => {
      const rawEmail = order.customer?.email;
      const key = rawEmail ? normalizeEmail(rawEmail) : 'no-email';
      if (!grouped[key]) {
        grouped[key] = {
          email: rawEmail ? rawEmail.trim() : 'No Email',
          customerName: order.customer
            ? `${order.customer.first_Name} ${order.customer.last_Name}`
            : 'Unknown',
          orders: []
        };
      }
      grouped[key].orders.push(order);
    });
    return Object.values(grouped);
  }, [selectedOrderDetails]);

  // Generate PDF with same format as InvoiceDetails.js
  const generateInvoicePDF = async (orderId) => {
    try {
      const token = await getValidToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`https://backend.hira-tech.co.uk/order/invoice/${orderId}/`, {
        headers: headers,
      });

      if (response.status === 401) {
        const newToken = await getValidToken();
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
          const retryResponse = await fetch(`https://backend.hira-tech.co.uk/order/invoice/${orderId}/`, {
            headers: headers,
          });
          if (!retryResponse.ok) throw new Error(`Failed to fetch invoice for order ${orderId}`);
          const invoiceData = await retryResponse.json();
          return { pdfDataUri: createPDF(invoiceData), invoiceData };
        }
      }

      if (!response.ok) throw new Error(`Failed to fetch invoice for order ${orderId}`);
      const invoiceData = await response.json();
      return { pdfDataUri: createPDF(invoiceData), invoiceData };
    } catch (error) {
      console.error(`Error generating PDF for order ${orderId}:`, error);
      throw error;
    }
  };

  // Create PDF with the same format as InvoiceDetails.js
  const createPDF = (invoiceData) => {
    const doc = new jsPDF("p", "mm", "a4");
    const logo = "/logo.ico";
    const invoiceNumber = `${invoiceData.invoice_number}`;
    const trackingNumber = invoiceData.order_details?.order_Tracking_Id || "N/A";

    try {
      doc.addImage(logo, "PNG", 7, 10, 60, 30);
    } catch (e) {
      console.warn("Logo not found, skipping");
    }

    doc.text(" ", 10, 70);
    const headerX = 12;
    let y = 45;

    const companyName = company?.company_name || 'Dental Lab';
    const companyAddress1 = company?.address_line1 || '';
    const companyAddress2 = company?.address_line2 || '';

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(companyName, headerX, y);
    doc.setFontSize(10);
    y += 7;
    doc.text(companyAddress1, headerX, y);
    y += 5;
    doc.text(companyAddress2, headerX, y);
    y += 8;
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(`Invoice #: ${invoiceNumber}`, headerX, y);

    const customerName = invoiceData.customer
      ? `${invoiceData.customer.first_Name || ''} ${invoiceData.customer.last_Name || ''}`
      : "Unknown Customer";

    const customerPhone = invoiceData.customer
      ? `${invoiceData.customer.phone_Number || "N/A"}`
      : "N/A";

    const addressParts = [];
    if (invoiceData?.address) {
      if (invoiceData.address.house_Number) addressParts.push(invoiceData.address.house_Number);
      if (invoiceData.address.street_Number) addressParts.push(invoiceData.address.street_Number);
      if (invoiceData.address.area) addressParts.push(invoiceData.address.area);
      if (invoiceData.address.city) addressParts.push(invoiceData.address.city);
    }
    const deliveryAddress = addressParts.length > 0
      ? addressParts.join(' ')
      : "No delivery address available";

    y += 10;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Ship-To-Party", headerX, y);
    y += 7;
    doc.setFont("helvetica", "normal");
    doc.text(customerName, headerX, y);
    y += 5;

    const addressLines = doc.splitTextToSize(deliveryAddress, 80);
    addressLines.forEach((line) => {
      doc.text(line, headerX, y);
      y += 5;
    });

    doc.text(`Contact: ${customerPhone}`, headerX, y);
    y += 5;

    if (invoiceData.order_details?.courier_service) {
      doc.text(`Courier Service: ${invoiceData.order_details.courier_service}`, headerX, y);
      y += 5;
    }

    const boxX = 120;
    const boxWidth = 75;
    const boxHeight = 70;
    doc.setDrawColor(0, 74, 173);
    doc.rect(boxX, 40, boxWidth, boxHeight);

    doc.setFillColor(0, 74, 173);
    doc.rect(boxX, 40, boxWidth, 10, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Invoice", boxX + 5, 47);
    doc.text(invoiceNumber, boxX + boxWidth - 5, 47, { align: "right" });

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    let invoiceY = 60;

    doc.text(`Date: ${invoiceData.issue_date || ''}`, boxX + 5, invoiceY);
    invoiceY += 5;
    doc.text("Page: 1 of 1", boxX + 5, invoiceY);
    invoiceY += 5;

    if (invoiceData.order_details) {
      doc.text(`Order ID: ${invoiceData.order_details.order_Id || ''}`, boxX + 5, invoiceY);
      invoiceY += 5;
      doc.text(`Tracking ID: ${trackingNumber}`, boxX + 5, invoiceY);
      invoiceY += 5;
      doc.text(`Courier Service: ${invoiceData.order_details.courier_service || ''}`, boxX + 5, invoiceY);
      invoiceY += 5;
    }

    doc.text(
      `Contact: ${invoiceData.customer ? invoiceData.customer.email || '' : "Unknown"}`,
      boxX + 5,
      invoiceY
    );
    invoiceY += 5;

    if (invoiceData.order_details) {
      doc.text(
        `Customer Reference: ${invoiceData.order_details.customer_Reference || "N/A"}`,
        boxX + 5,
        invoiceY
      );
    }
    invoiceY += 5;

    doc.setDrawColor(0, 74, 173);
    doc.line(boxX, invoiceY + 2, boxX + boxWidth, invoiceY + 2);
    invoiceY += 7;

    if (invoiceData.order_details) {
      doc.text(`Agent: ${invoiceData.order_details.agent_Name || ''}`, boxX + 5, invoiceY);
      invoiceY += 5;
      doc.text(`Mobile phone: ${invoiceData.order_details.agent_number || ''}`, boxX + 5, invoiceY);
    }

    const tableData = invoiceData.ordered_products?.map((item) => [
      item.product_Id?.product_Id || "N/A",
      item.product_Id?.product_Name || "N/A",
      item.quantity || "N/A",
      item.price_per_unit ? `Rs.${item.price_per_unit}` : "0",
      item.total_price ? `Rs.${item.total_price}` : "0",
    ]) || [];

    autoTable(doc, {
      startY: 120,
      head: [["Item", "Material/Description", "Quantity", "Price/SU", "Value"]],
      body: tableData,
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: "#004aad", textColor: 255 },
    });

    const finalY = doc.lastAutoTable.finalY || 120;

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 74, 173);

    if (invoiceData.order_details?.subtotal) {
      doc.text("Net Value: Rs." + invoiceData.order_details.subtotal, 150, finalY + 10);
    }

    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(8);
    doc.text("Terms of payment: 30 days end-of-month", 10, finalY + 35);

    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(10, finalY + 45, 200, finalY + 45);

    const footerY = finalY + 50;
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");

    if (company) {
      doc.text(
        `Website: ${company.website || ''} - E-Mail: ${company.email || ''}`,
        105,
        footerY,
        { align: "center" }
      );
    }

    const bankDetailsY = footerY + 8;
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("Bank Details:", 10, bankDetailsY);
    doc.setFont("helvetica", "normal");

    let currentY = bankDetailsY + 4;

    if (company) {
      doc.text(`Bank Name: ${company.bank_name || ''}`, 10, currentY);
      currentY += 4;
      doc.text(`Account Title: ${company.account_title || ''}`, 10, currentY);
      currentY += 4;
      doc.text(`Account Number: ${company.account_number || ''}`, 10, currentY);
      currentY += 4;
      doc.text(`Branch: ${company.branch || ''}`, 10, currentY);
      currentY += 4;
      doc.text(`IBAN: ${company.iban || ''}`, 10, currentY);
    }

    return doc.output('datauristring');
  };

  // Send batch invoice email using the batch endpoint
  const sendBatchInvoiceEmail = async (customerEmail, invoicesWithPdfs) => {
    try {
      const token = await getValidToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const batchData = {
        recipient_email: customerEmail,
        invoices: invoicesWithPdfs.map(({ invoiceData, pdfDataUri }) => ({
          invoice_number: invoiceData.invoice_number,
          pdf_base64: pdfDataUri.split(',')[1],
          tracking_number: invoiceData.order_details?.order_Tracking_Id || 'N/A'
        }))
      };

      let response = await fetch(
        'https://backend.hira-tech.co.uk/order/invoice/send-batch-email/',
        {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(batchData),
        }
      );

      if (response.status === 401) {
        const newToken = await getValidToken();
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
          response = await fetch(
            'https://backend.hira-tech.co.uk/order/invoice/send-batch-email/',
            {
              method: 'POST',
              headers: headers,
              body: JSON.stringify(batchData),
            }
          );
        }
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error(`Error sending batch invoice email to ${customerEmail}:`, error);
      throw error;
    }
  };

  // Show Sweet Alert for success
  const showSuccessAlert = (successCount, failedOrders, groupedByCustomer) => {
    if (failedOrders.length === 0) {
      let batchInfo = '';
      if (groupedByCustomer) {
        const batchSummary = Object.entries(groupedByCustomer)
          .filter(([email, orders]) => orders.length > 1 && email !== 'no-email')
          .map(([email, orders]) =>
            `• ${email}: ${orders.length} invoices in 1 email`
          );
        if (batchSummary.length > 0) {
          batchInfo = `
            <div style="margin-top: 10px; padding: 10px; background: #e7f3ff; border-radius: 6px; text-align: left;">
              <p style="margin: 0 0 5px 0; font-weight: 600; color: #004aad;">Batch Summary:</p>
              ${batchSummary.map(s => `<p style="margin: 3px 0; font-size: 14px;">${s}</p>`).join('')}
            </div>
          `;
        }
      }

      Swal.fire({
        icon: 'success',
        title: 'Invoices Sent Successfully!',
        html: `
          <div style="text-align: left;">
            <p><strong>${successCount}</strong> invoice(s) have been sent successfully.</p>
            <p style="color: #28a745; margin-top: 10px;">All invoices were sent to the customers' email addresses.</p>
            ${batchInfo}
          </div>
        `,
        confirmButtonColor: '#28a745',
        confirmButtonText: 'OK',
        timer: 5000,
        timerProgressBar: true,
      });
    } else {
      let failedHtml = failedOrders.map((failedOrder) => `
        <div style="background: #f8d7da; padding: 8px; margin: 5px 0; border-radius: 4px; border-left: 3px solid #dc3545;">
          <strong>${failedOrder.trackingId}</strong>: ${failedOrder.reason}
        </div>
      `).join('');

      Swal.fire({
        icon: 'warning',
        title: 'Partial Success',
        html: `
          <div style="text-align: left;">
            <p><strong>${successCount}</strong> invoice(s) sent successfully.</p>
            <p><strong style="color: #dc3545;">${failedOrders.length}</strong> invoice(s) failed.</p>
            <div style="margin-top: 15px; max-height: 200px; overflow-y: auto;">
              ${failedHtml}
            </div>
          </div>
        `,
        confirmButtonColor: '#ffc107',
        confirmButtonText: 'OK',
      });
    }
  };

  // Show error Sweet Alert
  const showErrorAlert = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Failed to Send Invoices',
      html: `
        <div style="text-align: left;">
          <p>${message}</p>
          <p style="color: #dc3545; margin-top: 10px;">Please try again or contact support.</p>
        </div>
      `,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'OK',
    });
  };

  // Show loading Sweet Alert
  const showLoadingAlert = (totalOrders) => {
    Swal.fire({
      title: 'Sending Invoices...',
      html: `
        <div style="display: flex; flex-direction: column; align-items: center;">
          <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem; margin-bottom: 15px;">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p>Please wait while we send your invoices.</p>
          <p style="font-size: 14px; color: #6c757d;">Preparing ${totalOrders} invoice(s) for delivery...</p>
        </div>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      showCancelButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
  };

  const handleSendInvoice = () => {
    if (selectedOrders.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'No Orders Selected',
        text: 'Please select at least one order to send invoice.',
        confirmButtonColor: '#004aad',
      });
      return;
    }
    setShowInvoiceModal(true);
  };

  const handleConfirmSend = async () => {
    const selectedOrdersToSend = getSelectedOrderDetails();

    const groupedByCustomer = {};
    selectedOrdersToSend.forEach(order => {
      const rawEmail = order.customer?.email;
      const normalized = normalizeEmail(rawEmail);

      if (normalized) {
        if (!groupedByCustomer[normalized]) {
          groupedByCustomer[normalized] = {
            displayEmail: rawEmail.trim(),
            orders: []
          };
        }
        groupedByCustomer[normalized].orders.push(order);
      } else {
        if (!groupedByCustomer['no-email']) {
          groupedByCustomer['no-email'] = {
            displayEmail: 'no-email',
            orders: []
          };
        }
        groupedByCustomer['no-email'].orders.push(order);
      }
    });

    showLoadingAlert(selectedOrdersToSend.length);

    setIsSending(true);
    setSendStatus(null);

    let successCount = 0;
    let failedOrders = [];

    try {
      for (const [normalizedEmail, group] of Object.entries(groupedByCustomer)) {
        const { displayEmail, orders } = group;

        if (normalizedEmail === 'no-email') {
          orders.forEach(order => {
            failedOrders.push({
              orderId: order.order_Id,
              trackingId: order.order_Tracking_Id,
              reason: 'No customer email found'
            });
          });
          continue;
        }

        try {
          const pdfsWithData = [];
          let groupFailed = false;

          for (const order of orders) {
            try {
              const result = await generateInvoicePDF(order.order_Id);
              pdfsWithData.push({
                orderId: order.order_Id,
                trackingId: order.order_Tracking_Id,
                invoiceData: result.invoiceData,
                pdfDataUri: result.pdfDataUri
              });
            } catch (error) {
              groupFailed = true;
              failedOrders.push({
                orderId: order.order_Id,
                trackingId: order.order_Tracking_Id,
                reason: error.message || 'Failed to generate PDF'
              });
            }
          }

          if (pdfsWithData.length > 0 && !groupFailed) {
            try {
              const batchResult = await sendBatchInvoiceEmail(
                displayEmail,
                pdfsWithData
              );

              if (batchResult.success) {
                successCount += pdfsWithData.length;
              } else {
                pdfsWithData.forEach(pdf => {
                  failedOrders.push({
                    orderId: pdf.orderId,
                    trackingId: pdf.trackingId,
                    reason: batchResult.message || 'Failed to send batch email'
                  });
                });
              }
            } catch (error) {
              console.error('Batch send failed for:', displayEmail, error);

              // Fallback to individual sends
              for (const pdfData of pdfsWithData) {
                try {
                  const token = await getValidToken();
                  const headers = {
                    'Content-Type': 'application/json',
                  };
                  if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                  }

                  const response = await fetch(
                    `https://backend.hira-tech.co.uk/order/invoice/${pdfData.invoiceData.invoice_number}/send-email/`,
                    {
                      method: 'POST',
                      headers: headers,
                      body: JSON.stringify({
                        pdf_base64: pdfData.pdfDataUri.split(',')[1],
                        recipient_email: displayEmail,
                      }),
                    }
                  );

                  if (response.ok) {
                    successCount++;
                  } else {
                    failedOrders.push({
                      orderId: pdfData.orderId,
                      trackingId: pdfData.trackingId,
                      reason: 'Failed to send individual email'
                    });
                  }
                } catch (error) {
                  failedOrders.push({
                    orderId: pdfData.orderId,
                    trackingId: pdfData.trackingId,
                    reason: error.message || 'Fallback send failed'
                  });
                }
              }
            }
          }
        } catch (error) {
          orders.forEach(order => {
            failedOrders.push({
              orderId: order.order_Id,
              trackingId: order.order_Tracking_Id,
              reason: error.message || 'Group processing failed'
            });
          });
        }
      }

      Swal.close();

      const groupedForSummary = Object.fromEntries(
        Object.entries(groupedByCustomer).map(([key, group]) => [
          key === 'no-email' ? 'no-email' : group.displayEmail,
          group.orders
        ])
      );

      if (failedOrders.length === 0) {
        showSuccessAlert(successCount, [], groupedForSummary);
        setTimeout(() => {
          setShowInvoiceModal(false);
          setSelectedOrders([]);
          setSelectAll(false);
          setSendStatus(null);
        }, 1000);
      } else {
        showSuccessAlert(successCount, failedOrders, groupedForSummary);
        setSendStatus({
          success: false,
          message: `${successCount} sent successfully, ${failedOrders.length} failed.`,
          failedOrders: failedOrders
        });
      }

    } catch (error) {
      console.error('Error sending invoices:', error);
      Swal.close();
      showErrorAlert(error.message || 'An unexpected error occurred');
      setSendStatus({
        success: false,
        message: `Error: ${error.message}`
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleApplyDateCustomerFilter = () => {
    if (startDate || endDate || selectedCustomer) {
      const ordersToSelect = filteredOrders.map(order => order.order_Id);
      setSelectedOrders(ordersToSelect);
      setSelectAll(ordersToSelect.length > 0 && ordersToSelect.length === filteredOrders.length);
    }
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setSelectedCustomer("");
    setSelectedOrders([]);
    setSelectAll(false);
  };

  const handleCloseModal = () => {
    if (!isSending) {
      setShowInvoiceModal(false);
      setSendStatus(null);
    }
  };

  const handleCancel = (orderId) => {
    const orderToUpdate = filteredOrders.find(order => order.order_Id === orderId);

    const updateData = {
      id: orderId,
      order_Status: "Canceled",
      customer: orderToUpdate.customer,
      payment: orderToUpdate.payment,
      total_Amount: orderToUpdate.total_Amount,
    };

    confirmAlert({
      title: "Confirm to Cancel Order",
      message: "Are you sure to cancel this order?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            const res = await updateOrder(updateData);
            if (res.error) {
              console.log(res.error.error);
            } else {
              console.log("Order updated:", res.data);
            }
          },
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const handleNavigate = (id) => {
    navigate(`/admin/order/details/${id}`);
  };

  if (isLoading) return <p>Loading...</p>;

  if (isError) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p style={{ color: 'red', fontSize: '18px' }}>Failed to load orders</p>
        <p>{error?.data?.detail || error?.message || 'Please check your connection'}</p>
        <button
          onClick={() => refetch()}
          style={{
            padding: '10px 20px',
            backgroundColor: '#004aad',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginTop: '10px'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="order-container">
        <h2>Orders</h2>

        <div className="search-row">
          <input
            type="text"
            className="search-bar"
            placeholder="Search by Tracking No./ Customer / Ref."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-row">
          <select
            className="filter-item"
            onChange={(e) => setSelectedStatus(e.target.value)}
            value={selectedStatus}
          >
            <option value="">Status</option>
            <option value="Pending">Pending</option>
            <option value="Order Placed">Order Placed</option>
            <option value="Under Package">Under Package</option>
            <option value="On The Way To Deliver">On The Way To Deliver</option>
            <option value="Delivered">Delivered</option>
            <option value="Canceled">Canceled</option>
          </select>
          <input
            className="filter-item"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start Date"
          />
          <input
            className="filter-item"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End Date"
          />
          <select
            className="filter-item"
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
          >
            <option value="">Select Customer</option>
            {uniqueCustomers.map((customer, index) => (
              <option key={index} value={customer.email}>
                {customer.name} ({customer.email})
              </option>
            ))}
          </select>
          <button
            className="filter-apply-btn"
            onClick={handleApplyDateCustomerFilter}
            title="Select all orders matching the date range and customer"
          >
            <FaFilter style={{ marginRight: '8px' }} />
            Apply Filters & Select All
          </button>
          <button
            className="filter-clear-btn"
            onClick={handleClearFilters}
            title="Clear all filters and selections"
          >
            <FaUndo style={{ marginRight: '8px' }} />
            Clear Filters
          </button>
        </div>

        {selectedOrders.length > 0 && (
          <div className="invoice-button-container">
            <button
              className="send-invoice-btn"
              onClick={handleSendInvoice}
              disabled={isSending}
            >
              {isSending ? (
                <>
                  <FaSpinner className="spinning" style={{ marginRight: '8px' }} />
                  Sending...
                </>
              ) : (
                <>
                  <FaEnvelope style={{ marginRight: '8px' }} />
                  Send Invoice ({selectedOrders.length})
                </>
              )}
            </button>
          </div>
        )}

        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    disabled={currentOrders.length === 0}
                  />
                </th>
                <th><FaCalendarAlt style={{ marginRight: '5px' }} />Date</th>
                <th><FaBox style={{ marginRight: '5px' }} />Order Tracking ID</th>
                <th>Customer Reference</th>
                <th><FaUser style={{ marginRight: '5px' }} />Customer</th>
                <th>Status</th>
                <th>Total</th>
                <th>Note</th>
                <th><FaInfoCircle style={{ marginRight: '5px' }} />Address</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentOrders.map((order, index) => (
                <tr key={index}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedOrders.includes(order.order_Id)}
                      onChange={() => handleSelectOrder(order.order_Id)}
                    />
                  </td>
                  <td>{order.order_Placment_Date}</td>
                  <td>{order.order_Tracking_Id}</td>
                  <td>{order.customer_Reference}</td>
                  <td>{order.customer ? `${order.customer.first_Name} ${order.customer.last_Name}` : "N/A"}</td>
                  <td>{order.order_Status}</td>
                  <td>{formatPrice(order.total_Amount)}</td>
                  <td>{order.note}</td>
                  <td>
                    H: {order.address.house_Number}, St: {order.address.street_Number}, Area:{" "}
                    {order.address.area}, City: {order.address.city}
                  </td>
                  <td className="arrow-cell">
                    <button
                      style={{ color: "red", marginRight: "10px" }}
                      onClick={() => handleCancel(order.order_Id)}
                    >
                      <GiCancel />
                    </button>
                    <button
                      className="navigate-button"
                      onClick={() => handleNavigate(order.order_Id)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      <FaCircleChevronRight
                        className="arrow-icon"
                        style={{ color: "#004aad", transition: "transform 0.3s ease-in-out" }}
                        onMouseEnter={(e) => (e.currentTarget.style.transform = "translateX(5px)")}
                        onMouseLeave={(e) => (e.currentTarget.style.transform = "translateX(0)")}
                      />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="page-button"
            style={{ backgroundColor: "#004aad", color: "white" }}
          >
            Previous
          </button>
          <span className="page-info">Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="page-button"
            style={{ backgroundColor: "#004aad", color: "white" }}
          >
            Next
          </button>
        </div>
      </div>

      {/* Invoice Modal */}
      {showInvoiceModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FaEnvelope style={{ marginRight: '10px', color: '#004aad' }} />Send Invoices</h3>
              <button
                className="modal-close-btn"
                onClick={handleCloseModal}
                disabled={isSending}
              >
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              {groupedOrders.some(group => group.orders.length > 1) && (
                <div className="batch-info-banner">
                  <FaInfoCircle style={{ marginRight: '8px' }} />
                  <strong>Batch Sending Active:</strong> Multiple invoices for the same customer will be sent in a single email.
                </div>
              )}

              {sendStatus && (
                <div className={`send-status ${sendStatus.success ? 'success' : 'error'}`}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    {sendStatus.success ? (
                      <FaCheckCircle style={{ color: '#28a745', marginTop: '2px' }} />
                    ) : (
                      <FaExclamationTriangle style={{ color: '#dc3545', marginTop: '2px' }} />
                    )}
                    <div>
                      <span>{sendStatus.message}</span>
                      {sendStatus.failedOrders && sendStatus.failedOrders.length > 0 && (
                        <div className="failed-orders">
                          <p><strong>Failed Orders:</strong></p>
                          <ul>
                            {sendStatus.failedOrders.map((failedOrder, idx) => (
                              <li key={idx}>
                                Tracking: {failedOrder.trackingId} - {failedOrder.reason}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!sendStatus && (
                <>
                  <div className="modal-summary">
                    <p className="order-count">
                      You have selected <strong>{selectedOrders.length}</strong> order(s) to send invoices
                    </p>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                      {groupedOrders.length} customer(s) will receive {groupedOrders.map(g => g.orders.length).join(', ')} invoice(s) each
                    </p>
                  </div>

                  <div className="selected-orders-list">
                    <h4>Selected Orders by Customer:</h4>
                    {groupedOrders.map((group, groupIndex) => (
                      <div key={groupIndex} className="customer-group">
                        <div className="customer-group-header">
                          <span className="customer-name">
                            <FaUser style={{ marginRight: '6px' }} />
                            {group.customerName}
                          </span>
                          <span className="customer-email">
                            <FaEnvelope style={{ marginRight: '6px' }} />
                            {group.email}
                          </span>
                          <span className="order-badge">
                            <FaFileInvoice style={{ marginRight: '4px' }} />
                            {group.orders.length} invoice{group.orders.length > 1 ? 's' : ''}
                            {group.orders.length > 1 && ' 📦'}
                          </span>
                        </div>
                        <ul>
                          {group.orders.map((order, orderIndex) => (
                            <li key={orderIndex} className="order-item">
                              <span className="order-number">{orderIndex + 1}.</span>
                              <span className="order-tracking">
                                <strong>Tracking:</strong> {order.order_Tracking_Id || 'N/A'}
                              </span>
                              <span className="order-amount">
                                <strong>Amount:</strong> {formatPrice(order.total_Amount)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>

                  <div className="modal-total">
                    <p>Total Amount: <strong>{formatPrice(selectedOrderDetails.reduce((sum, order) => sum + order.total_Amount, 0))}</strong></p>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              {!sendStatus && (
                <>
                  <button
                    className="modal-cancel-btn"
                    onClick={handleCloseModal}
                    disabled={isSending}
                  >
                    <FaTimes style={{ marginRight: '6px' }} />
                    Cancel
                  </button>
                  <button
                    className="modal-send-btn"
                    onClick={handleConfirmSend}
                    disabled={isSending}
                  >
                    {isSending ? (
                      <>
                        <FaSpinner className="spinning" style={{ marginRight: '6px' }} />
                        Sending...
                      </>
                    ) : (
                      <>
                        <MdSend style={{ marginRight: '6px' }} />
                        Send Invoices
                      </>
                    )}
                  </button>
                </>
              )}
              {sendStatus && (
                <button
                  className="modal-close-btn-modal"
                  onClick={handleCloseModal}
                >
                  <FaTimes style={{ marginRight: '6px' }} />
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .search-row,
        .filter-row {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 15px;
          align-items: center;
        }
        .search-bar {
          flex: 1;
          min-width: 250px;
          padding: 12px;
          border-radius: 6px;
          border: 1px solid #ccc;
          font-size: 14px;
        }
        .filter-item {
          flex: 1;
          min-width: 120px;
          padding: 10px;
          border-radius: 6px;
          border: 1px solid #ccc;
          font-size: 14px;
        }
        .filter-apply-btn {
          padding: 10px 20px;
          background: linear-gradient(135deg, #004aad, #0066cc);
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
        }
        .filter-apply-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 74, 173, 0.3);
        }
        .filter-clear-btn {
          padding: 10px 20px;
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
        }
        .filter-clear-btn:hover {
          background: #5a6268;
          transform: translateY(-2px);
        }

        .invoice-button-container {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 15px;
        }
        .send-invoice-btn {
          padding: 12px 24px;
          background: linear-gradient(135deg, #28a745, #20c997);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
          display: flex;
          align-items: center;
        }
        .send-invoice-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(40, 167, 69, 0.4);
        }
        .send-invoice-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .table-wrapper {
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
        }
        th,
        td {
          padding: 10px;
          border: 1px solid #ddd;
          text-align: left;
        }
        th {
          background: #004aad;
          color: white;
        }

        th input[type="checkbox"],
        td input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
          accent-color: #004aad;
        }
        th input[type="checkbox"]:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-top: 20px;
        }
        .page-button {
          padding: 8px 12px;
          margin: 0 5px;
          border: none;
          background-color: #004aad;
          color: white;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }
        .page-button:disabled {
          cursor: not-allowed;
          background-color: #ddd;
          color: #666;
        }

        .batch-info-banner {
          background: linear-gradient(135deg, #e7f3ff, #d4e9ff);
          color: #004aad;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 16px;
          border-left: 4px solid #004aad;
          font-size: 14px;
          display: flex;
          align-items: center;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          animation: fadeIn 0.3s ease-in;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from { transform: translateY(50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .modal-container {
          background: white;
          border-radius: 12px;
          max-width: 800px;
          width: 90%;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.3s ease-out;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e0e0e0;
        }

        .modal-header h3 {
          margin: 0;
          color: #004aad;
          font-size: 20px;
          display: flex;
          align-items: center;
        }

        .modal-close-btn {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #666;
          transition: color 0.2s;
          padding: 0 8px;
          display: flex;
          align-items: center;
        }

        .modal-close-btn:hover:not(:disabled) {
          color: #333;
        }
        .modal-close-btn:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .modal-body {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
        }

        .modal-summary {
          background: #f8f9fa;
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .order-count {
          margin: 0;
          font-size: 16px;
          color: #333;
        }

        .order-count strong {
          color: #28a745;
          font-size: 18px;
        }

        .selected-orders-list {
          margin-bottom: 20px;
        }

        .selected-orders-list h4 {
          margin: 0 0 12px 0;
          color: #333;
          font-size: 16px;
        }

        .customer-group {
          margin-bottom: 12px;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          overflow: hidden;
        }

        .customer-group-header {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          background: #f0f7ff;
          border-bottom: 1px solid #e0e0e0;
        }

        .customer-name {
          font-weight: 600;
          color: #004aad;
          font-size: 15px;
          display: flex;
          align-items: center;
        }

        .customer-email {
          font-size: 14px;
          color: #555;
          display: flex;
          align-items: center;
        }

        .order-badge {
          background: #004aad;
          color: white;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
          margin-left: auto;
          display: flex;
          align-items: center;
        }

        .customer-group ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .order-item {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 12px;
          padding: 10px 16px;
          background: #fafafa;
          border-bottom: 1px solid #f0f0f0;
        }

        .order-item:last-child {
          border-bottom: none;
        }

        .order-item:hover {
          background: #f5f5f5;
        }

        .order-number {
          font-weight: 600;
          color: #004aad;
          min-width: 30px;
        }

        .order-tracking,
        .order-amount {
          font-size: 14px;
        }

        .order-tracking strong,
        .order-amount strong {
          color: #333;
        }

        .modal-total {
          background: #e7f3ff;
          padding: 16px;
          border-radius: 8px;
          text-align: right;
          border: 1px solid #b3d9ff;
        }

        .modal-total p {
          margin: 0;
          font-size: 16px;
          color: #004aad;
        }

        .modal-total strong {
          font-size: 18px;
          color: #004aad;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid #e0e0e0;
          background: #f8f9fa;
          border-radius: 0 0 12px 12px;
        }

        .modal-cancel-btn {
          padding: 10px 24px;
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: background 0.2s;
          display: flex;
          align-items: center;
        }

        .modal-cancel-btn:hover:not(:disabled) {
          background: #5a6268;
        }
        .modal-cancel-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .modal-send-btn {
          padding: 10px 28px;
          background: linear-gradient(135deg, #28a745, #20c997);
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.3s;
          display: flex;
          align-items: center;
        }

        .modal-send-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
        }
        .modal-send-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .modal-close-btn-modal {
          padding: 10px 28px;
          background: #004aad;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          display: flex;
          align-items: center;
        }

        .modal-close-btn-modal:hover {
          background: #003a8a;
        }

        .send-status {
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 16px;
        }

        .send-status.success {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .send-status.error {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .failed-orders {
          margin-top: 12px;
          padding: 12px;
          background: rgba(0,0,0,0.05);
          border-radius: 4px;
        }

        .failed-orders ul {
          margin: 8px 0 0 0;
          padding-left: 20px;
        }

        .failed-orders li {
          margin: 4px 0;
          font-size: 13px;
        }

        .selected-orders-list ul::-webkit-scrollbar {
          width: 6px;
        }

        .selected-orders-list ul::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }

        .selected-orders-list ul::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 10px;
        }

        .selected-orders-list ul::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        @media (max-width: 768px) {
          .order-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 6px;
          }

          .modal-container {
            width: 95%;
            max-height: 90vh;
          }

          .modal-footer {
            flex-direction: column-reverse;
          }

          .modal-cancel-btn,
          .modal-send-btn,
          .modal-close-btn-modal {
            width: 100%;
            text-align: center;
            justify-content: center;
          }

          .customer-group-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .order-badge {
            margin-left: 0;
          }
        }
      `}</style>
    </>
  );
}